import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  createOptionalLevel,
  importOptionalLevelLocations,
  listOptionalLevels,
  setOptionalLevelInstance,
  updateOptionalLevel,
} from '../countryManagement.service';
import { resultMessage } from '../../../../service/apiResult';
import { primaryIconColor } from '../../../styles/iconStyles';

/** Readable names for the eK Acre Growth instance keys (app.eag.instances.* on the backend). */
const INSTANCE_LABELS = {
  'ek-acre-growth': 'FF Bio (eK Acre Growth)',
  'eag-abhigyan': 'Abhigyan India',
  'eag-spisa': 'SPISA',
  'eag-ifeed': 'iFEED Ghana',
  'eag-bluestone': 'Bluestone',
  sanghamitra: 'Sanghamitra',
};

const instanceLabel = (key) => INSTANCE_LABELS[key] || key;

const buttonSx = { height: 36, borderRadius: '10px', textTransform: 'none', fontSize: 14, fontWeight: 500 };

/**
 * One optional level: its place in the chain, how many places it holds, a switch per eK Acre Growth
 * instance, and the CSV import (always a dry run first, so nothing is written before the operator has
 * seen which rows match).
 */
function OptionalLevelCard({ countryId, level, levelNames, onChanged }) {
  const [busyKey, setBusyKey] = useState(null);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);

  const toggleInstance = async (instanceKey, enabled) => {
    setBusyKey(instanceKey);
    setError(null);
    const result = await setOptionalLevelInstance(countryId, level.id, instanceKey, enabled);
    setBusyKey(null);
    if (!result.success) {
      setError(resultMessage(result, 'The switch could not be changed.'));
      return;
    }
    onChanged();
  };

  const toggleActive = async () => {
    setError(null);
    const result = await updateOptionalLevel(countryId, level.id, { active: !level.active });
    if (!result.success) {
      setError(resultMessage(result, 'The level could not be updated.'));
      return;
    }
    onChanged();
  };

  const runImport = async (dryRun) => {
    if (!file) return;
    setImporting(true);
    setError(null);
    const result = await importOptionalLevelLocations(countryId, level.id, file, dryRun);
    setImporting(false);
    if (!result.success) {
      setError(resultMessage(result, 'The file could not be imported.'));
      return;
    }
    if (dryRun) {
      setPreview(result.data);
      return;
    }
    setPreview(null);
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
    onChanged();
  };

  const columns = [...levelNames.slice(0, level.afterLevel), level.name, 'Code (optional)'];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.12)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Icon icon="mdi:map-marker-plus-outline" width="20" height="20" color={primaryIconColor} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{level.name}</Typography>
            <Chip
              size="small"
              variant="outlined"
              color={level.active ? 'success' : 'default'}
              label={level.active ? 'Active' : 'Inactive'}
            />
          </Box>
          <Typography variant="body2" color="textSecondary">
            Between {level.afterLevelName || `level ${level.afterLevel}`} and the next level · {level.locationCount} place(s)
          </Typography>
        </Box>
        <Button onClick={toggleActive} sx={{ ...buttonSx, color: '#294190' }}>
          {level.active ? 'Deactivate' : 'Activate'}
        </Button>
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Shown in</Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
        Only the apps switched on here show this level on their farmer address. Every other app, and the
        country&apos;s own levels, stay exactly as they are.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
        {level.instances.map((instance) => (
          <FormControlLabel
            key={instance.instanceKey}
            control={(
              <Switch
                checked={instance.enabled}
                disabled={busyKey !== null || !level.active}
                onChange={(e) => toggleInstance(instance.instanceKey, e.target.checked)}
              />
            )}
            label={instanceLabel(instance.instanceKey)}
          />
        ))}
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>Import places</Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
        CSV with a header row, then one row per {level.name}: {columns.join(', ')}. Names are matched to
        this country&apos;s locations; rows that match nothing are listed and skipped, never created.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            setFile(e.target.files?.[0] || null);
            setPreview(null);
          }}
        />
        <Button
          variant="outlined"
          disabled={!file || importing}
          onClick={() => runImport(true)}
          startIcon={importing ? <CircularProgress size={14} /> : <Icon icon="mdi:file-search-outline" width="18" />}
          sx={buttonSx}
        >
          Check file
        </Button>
      </Box>

      {preview && (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', backgroundColor: '#F7F9FF' }}>
          <Typography variant="body2">
            {preview.rows} row(s): <strong>{preview.created}</strong> new, {preview.existing} already there,{' '}
            <Box component="span" sx={{ color: preview.unmatched ? 'error.main' : 'inherit' }}>
              {preview.unmatched} not matched
            </Box>
            .
          </Typography>
          {preview.unmatchedRows?.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, maxHeight: 160, overflowY: 'auto' }}>
              {preview.unmatchedRows.map((row) => (
                <Typography key={row} component="li" variant="caption" color="textSecondary">{row}</Typography>
              ))}
            </Box>
          )}
          <Button
            variant="contained"
            disabled={importing || preview.created === 0}
            onClick={() => runImport(false)}
            sx={{ ...buttonSx, mt: 1.5 }}
          >
            Import {preview.created} new place(s)
          </Button>
        </Box>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>{error}</Typography>
      )}
    </Paper>
  );
}

/**
 * Optional address levels of a country (A-01): a level beside the country's own chain, such as Gram
 * Panchayat after Block for India, shown only to the eK Acre Growth apps it is switched on for.
 */
function OptionalLevelsManager({ countryId, levels: administrativeLevels = [] }) {
  const [optionalLevels, setOptionalLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAfter, setNewAfter] = useState('');
  const [saving, setSaving] = useState(false);

  const levelNames = [...administrativeLevels]
    .sort((a, b) => a.level - b.level)
    .map((l) => l.name);

  const load = useCallback(async () => {
    if (!countryId) return;
    const result = await listOptionalLevels(countryId);
    setLoading(false);
    if (!result.success) {
      setError(resultMessage(result, 'Could not read the optional levels.'));
      return;
    }
    setError(null);
    setOptionalLevels(Array.isArray(result.data) ? result.data : []);
  }, [countryId]);

  useEffect(() => {
    load();
  }, [load]);

  const addLevel = async () => {
    setSaving(true);
    const result = await createOptionalLevel(countryId, { name: newName.trim(), afterLevel: Number(newAfter) });
    setSaving(false);
    if (!result.success) {
      setError(resultMessage(result, 'The level could not be added.'));
      return;
    }
    setAddOpen(false);
    setNewName('');
    setNewAfter('');
    load();
  };

  if (!countryId) return null;

  return (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="textSecondary">Loading optional levels…</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {optionalLevels.length === 0 && (
            <Typography variant="body2" color="textSecondary">This country has no optional levels.</Typography>
          )}
          {optionalLevels.map((level) => (
            <OptionalLevelCard
              key={level.id}
              countryId={countryId}
              level={level}
              levelNames={levelNames}
              onChanged={load}
            />
          ))}
        </Box>
      )}

      <Button
        onClick={() => setAddOpen(true)}
        startIcon={<Icon icon="mdi:plus" width="18" height="18" />}
        disabled={administrativeLevels.length === 0}
        sx={{ ...buttonSx, mt: 2, height: 40, borderRadius: '12px', backgroundColor: '#DFE5F6', color: '#294190' }}
      >
        Add optional level
      </Button>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5 }}>{error}</Typography>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add optional level</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Name"
            placeholder="Gram Panchayat"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="optional-level-after">Placed after</InputLabel>
            <Select
              labelId="optional-level-after"
              label="Placed after"
              value={newAfter}
              onChange={(e) => setNewAfter(e.target.value)}
            >
              {[...administrativeLevels].sort((a, b) => a.level - b.level).map((l) => (
                <MenuItem key={l.level} value={l.level}>{`Level ${l.level}: ${l.name}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={addLevel}
            variant="contained"
            disabled={saving || !newName.trim() || newAfter === ''}
            sx={{ textTransform: 'none', borderRadius: '10px' }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OptionalLevelsManager;
